import { logBitacora, ACCIONES, MODULOS } from "@/data/adminBitacoraStore";
import { api } from "@/services/api";


/**
 * Sesión y usuarios administrativos.
 *
 * Ola 5: autenticación por cookie HttpOnly.
 * - `loginAdmin` es async: hace POST al backend; la cookie `access_token`
 *   se emite y se guarda automáticamente por el navegador. Sólo se
 *   persiste en `localStorage` el snapshot NO SENSIBLE de la sesión
 *   (usuario/nombre/rol) para pintar el header de la UI.
 * - `logoutAdmin` llama al backend para borrar la cookie y luego limpia
 *   el snapshot local.
 * - Los reads (`getAdminSession`, `isAdminAuthenticated`) siguen siendo
 *   síncronos porque leen del cache local; la fuente de verdad es el
 *   backend que rechaza requests si la cookie es inválida.
 */

const KEY_SESSION = "ct_admin_session_v1";

export const ADMIN_ROLES = {
  GERENCIA: "GERENCIA",
  SISTEMAS: "SISTEMAS",
  EVENTOS: "EVENTOS",
  MARKETING: "MARKETING",
};

export const ADMIN_ROLE_LABELS = {
  GERENCIA: "Gerencia",
  SISTEMAS: "Sistemas",
  EVENTOS: "Eventos",
  MARKETING: "Marketing",
};

/**
 * Módulos del portal administrativo. La clave es reutilizada por el sidebar
 * y por la matriz de permisos. Mantener sincronizado con `adminMenu.js`.
 */
export const ADMIN_MODULES = {
  dashboard: "dashboard",
  eventos: "eventos",
  usuarios: "usuarios",
  notificaciones: "notificaciones",
  bitacora: "bitacora",
  configuracion: "configuracion",
  reportes: "reportes",
  plantillasMesas: "plantillasMesas",
};

const ALL_MODULES_TRUE = Object.keys(ADMIN_MODULES).reduce((acc, key) => {
  acc[key] = true;
  return acc;
}, {});

export const ADMIN_PERMISSIONS = {
  GERENCIA: { ...ALL_MODULES_TRUE },
  SISTEMAS: { ...ALL_MODULES_TRUE },
  // P1 · EVENTOS: operación de eventos/mesas/boletos y comunicaciones, pero
  // SIN administración sensible (usuarios/roles, configuración, bitácora).
  EVENTOS: {
    ...ALL_MODULES_TRUE,
    usuarios: false,
    configuracion: false,
    bitacora: false,
  },
  // P1 · MARKETING: comunicación/contenido. SIN plantillas de mesas (Sprint 58)
  // y SIN administración sensible (usuarios/roles, configuración, bitácora).
  MARKETING: {
    ...ALL_MODULES_TRUE,
    plantillasMesas: false,
    usuarios: false,
    configuracion: false,
    bitacora: false,
  },
};

export const puedeAccederModulo = (rol, moduloKey) => {
  if (!rol || !moduloKey) return false;
  const matriz = ADMIN_PERMISSIONS[rol];
  if (!matriz) return false;
  return matriz[moduloKey] === true;
};

const isBrowser = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const readJson = (key, fallback) => {
  if (!isBrowser()) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
};

const removeKey = (key) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

/* -------------------- Compatibilidad -------------------- */

/**
 * Antes sembraba usuarios en localStorage. Ahora la fuente de verdad es
 * el backend (seed idempotente en el startup del server). Este export se
 * mantiene por compatibilidad con los consumidores existentes que aún
 * lo invocan en `useEffect`.
 */
export const seedAdminUsers = () => {
  /* no-op: los usuarios viven en el backend. */
  return [];
};

/* -------------------- Sesión -------------------- */

export const getAdminSession = () => {
  const s = readJson(KEY_SESSION, null);
  if (!s || !s.usuario || !s.rol) return null;
  return s;
};

/**
 * Login real contra `/api/auth/login`. Devuelve la sesión creada o
 * `null` si las credenciales fallan. Los errores de red retornan
 * `{ error: "network" }` para permitir mensajes específicos.
 */
export const loginAdmin = async (usuarioInput, passwordInput) => {
  const usuario = String(usuarioInput || "").trim().toLowerCase();
  const password = String(passwordInput || "");
  if (!usuario || !password) return null;

  try {
    const { data } = await api.post("/auth/login", { usuario, password });
    if (!data || !data.usuario) return null;
    const session = {
      usuario: data.usuario.usuario,
      nombre: data.usuario.nombre,
      rol: data.usuario.rol,
      fechaInicioSesion: new Date().toISOString(),
    };
    writeJson(KEY_SESSION, session);
    logBitacora({
      usuario: session.usuario,
      accion: ACCIONES.LOGIN,
      modulo: MODULOS.AUTH,
      detalle: "Sesión iniciada.",
    });
    return session;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401 || status === 403) return null;
    return { error: "network" };
  }
};

export const logoutAdmin = () => {
  const s = getAdminSession();
  removeKey(KEY_SESSION);
  if (s) {
    logBitacora({
      usuario: s.usuario,
      accion: ACCIONES.LOGOUT,
      modulo: MODULOS.AUTH,
      detalle: "Sesión finalizada.",
    });
  }
  // Fire-and-forget: notifica al backend para borrar la cookie HttpOnly.
  api.post("/auth/logout").catch(() => {});
};

// Sprint 47 · limpieza local sin llamar backend (usada por el listener
// global de sesión terminada por el servidor).
export const clearAdminSessionLocal = () => removeKey(KEY_SESSION);

export const isAdminAuthenticated = () => getAdminSession() !== null;

/* -------------------- CRUD Usuarios (backend real) -------------------- */

/**
 * Los endpoints admin `/api/usuarios/*` viven detrás de `require_admin`.
 * Estos helpers son async y devuelven la lista/objeto persistido por el
 * backend, o `{ error }` en fallo controlado.
 */

const KEY_USUARIOS = "ct_admin_usuarios_v1";

const readUsuariosCache = () => {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(KEY_USUARIOS);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const writeUsuariosCache = (arr) => writeJson(KEY_USUARIOS, arr);

export const hidratarUsuarios = async () => {
  const { api } = await import("@/services/api");
  try {
    const { data } = await api.get("/usuarios");
    if (Array.isArray(data)) {
      writeUsuariosCache(data);
      return data;
    }
  } catch {
    /* fallback silencioso */
  }
  return readUsuariosCache();
};

export const listarUsuarios = () => readUsuariosCache();

export const crearUsuario = async (payload) => {
  const { api } = await import("@/services/api");
  try {
    const { data } = await api.post("/usuarios", {
      usuario: (payload.usuario || "").trim().toLowerCase(),
      password: payload.password,
      rol: payload.rol,
      nombre: (payload.nombre || "").trim(),
      puesto: payload.puesto || "",
      correo: (payload.correo || "").trim(),
      telefono: (payload.telefono || "").trim(),
    });
    await hidratarUsuarios();
    return data;
  } catch (err) {
    const detail = err?.response?.data?.detail;
    if (detail === "duplicado") return { error: "duplicado" };
    return { error: detail || "network" };
  }
};

export const editarUsuario = async ({ usuario, cambios }) => {
  const { api } = await import("@/services/api");
  try {
    const { data } = await api.patch(`/usuarios/${usuario}`, cambios || {});
    await hidratarUsuarios();
    return data;
  } catch (err) {
    return { error: err?.response?.data?.detail || "network" };
  }
};

export const setUsuarioActivo = async (usuario, activo) => {
  const { api } = await import("@/services/api");
  try {
    const { data } = await api.patch(`/usuarios/${usuario}/activo`, { activo: !!activo });
    await hidratarUsuarios();
    return data;
  } catch (err) {
    return { error: err?.response?.data?.detail || "network" };
  }
};
